import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Download, Search } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CountryBadge,
  EmptyState,
  OperatorBadge,
  PageHeader,
  SkeletonRow,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TxnId,
} from '@/components/ui';
import { isApiError } from '@/lib/api/envelope';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import { useAuth } from '@/lib/auth/useAuth';
import { downloadCsv } from '@/lib/utils/csvExport';
import type { ResponseEnvelope } from '@/types/api';

interface VerificationRecord {
  id: string;
  clientId: string;
  clientName: string | null;
  mobileNumber: string;
  isNumberValid: boolean;
  createdAt: string;
  operatorId: string | null;
  countryCode: string | null;
  serviceType: string | null;
  latencyMs: number | null;
}

interface VerificationsPage {
  content: VerificationRecord[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

async function fetchVerifications(
  isClient: boolean,
  params: {
    page: number;
    size: number;
    sortDirection?: string;
    status?: boolean;
    clientId?: string;
    transactionId?: string;
    countryCode?: string;
    startDate?: string;
    endDate?: string;
  },
): Promise<VerificationsPage> {
  const endpoint = isClient ? Endpoints.dashboard.myVerifications : Endpoints.dashboard.verifications;
  // For the client-scoped endpoint, the backend uses the JWT claim — strip clientId from params.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { clientId: _clientId, ...paramsWithoutClientId } = params;
  const queryParams = isClient ? paramsWithoutClientId : params;
  const res = await api.get<ResponseEnvelope<VerificationsPage>>(endpoint, { params: queryParams });
  return unwrap(res);
}

export function VerificationsPage() {
  const { hasAnyRole } = useAuth();
  const isClient = hasAnyRole([
    'AUTHMATECH_CLIENT_ADMIN',
    'AUTHMATECH_CLIENT_OPERATOR',
    'AUTHMATECH_CLIENT_FINANCE',
    'AUTHMATECH_CLIENT_VIEWER',
  ]);

  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [clientIdFilter, setClientIdFilter] = useState('');
  const [transactionIdFilter, setTransactionIdFilter] = useState('');
  const [countryCodeFilter, setCountryCodeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const query = useQuery({
    queryKey: [
      'dashboard', 'verifications', page, statusFilter,
      clientIdFilter, transactionIdFilter, countryCodeFilter, startDate, endDate, isClient,
    ],
    queryFn: () =>
      fetchVerifications(isClient, {
        page,
        size: 25,
        sortDirection: 'desc',
        status: statusFilter === 'all' ? undefined : statusFilter === 'success',
        clientId: clientIdFilter || undefined,
        transactionId: transactionIdFilter || undefined,
        countryCode: countryCodeFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
  });

  const rows = query.data?.content ?? [];
  const totalPages = query.data?.totalPages ?? 1;

  function resetFilters() {
    setPage(0);
    setStatusFilter('all');
    setClientIdFilter('');
    setTransactionIdFilter('');
    setCountryCodeFilter('');
    setStartDate('');
    setEndDate('');
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      const exportData = await fetchVerifications(isClient, {
        page: 0,
        size: 5000,
        sortDirection: 'desc',
        status: statusFilter === 'all' ? undefined : statusFilter === 'success',
        clientId: clientIdFilter || undefined,
        transactionId: transactionIdFilter || undefined,
        countryCode: countryCodeFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const headers = ['ID', 'Client', 'Mobile', 'Operator', 'Country', 'Service', 'Result', 'Latency (ms)', 'Timestamp'];
      const csvRows = exportData.content.map((r) => [
        r.id,
        r.clientName ?? r.clientId,
        r.mobileNumber ?? '',
        r.operatorId ?? '',
        r.countryCode ?? '',
        r.id.startsWith('VNM-') ? 'Verify Number Plus' : 'Verify Number',
        r.isNumberValid ? 'MATCH' : 'NO MATCH',
        r.latencyMs ?? '',
        r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
      ]);
      downloadCsv(`verifications-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title={isClient ? 'My Verifications' : 'Verifications'}
        subtitle={isClient ? 'Your verification history — newest first.' : 'All verification attempts across all clients, newest first.'}
        actions={
          <Button variant="outline" size="sm" onClick={handleExport} disabled={isExporting || rows.length === 0}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {/* Status pills */}
          <div className="flex items-center gap-1.5 rounded-lg border border-border bg-surface-subtle p-1">
            {(['all', 'success', 'failed'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => { setStatusFilter(f); setPage(0); }}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-all ${
                  statusFilter === f
                    ? 'bg-surface shadow-sm text-fg ring-1 ring-border'
                    : 'text-fg-muted hover:text-fg'
                }`}
              >
                {f === 'all' ? 'All' : f === 'success' ? 'Match' : 'No match'}
              </button>
            ))}
          </div>

          {/* Transaction ID search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
            <input
              type="search"
              placeholder="Transaction ID…"
              value={transactionIdFilter}
              onChange={(e) => { setTransactionIdFilter(e.target.value); setPage(0); }}
              className="h-8 w-full rounded-lg border border-border bg-surface pl-8 pr-3 text-xs outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            />
          </div>

          {/* Client ID filter — hidden for AUTHMATECH_CLIENT */}
          {!isClient && (
            <div className="relative flex-1 min-w-[160px]">
              <input
                type="search"
                placeholder="Client ID…"
                value={clientIdFilter}
                onChange={(e) => { setClientIdFilter(e.target.value); setPage(0); }}
                className="h-8 w-full rounded-lg border border-border bg-surface px-3 text-xs outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
          )}

          {/* Country code filter */}
          {!isClient && (
            <div className="relative min-w-[120px]">
              <input
                type="search"
                placeholder="Country (e.g. JO)…"
                value={countryCodeFilter}
                onChange={(e) => { setCountryCodeFilter(e.target.value.toUpperCase()); setPage(0); }}
                className="h-8 w-full rounded-lg border border-border bg-surface px-3 text-xs outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
          )}

          {/* Date range */}
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
            className="h-8 rounded-lg border border-border bg-surface px-2 text-xs outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          />
          <span className="text-xs text-fg-subtle">→</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
            className="h-8 rounded-lg border border-border bg-surface px-2 text-xs outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          />

          <div className="ml-auto flex items-center gap-3">
            {query.data && (
              <span className="text-xs text-fg-subtle">
                {query.data.totalElements.toLocaleString()} records
              </span>
            )}
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-fg-muted hover:text-fg underline-offset-2 hover:underline"
            >
              Reset
            </button>
          </div>
        </div>
      </Card>

      <Card noPadding>
        {query.isLoading ? (
          <div>
            {[0, 1, 2, 3, 4, 5].map((i) => <SkeletonRow key={i} cols={isClient ? 7 : 9} />)}
          </div>
        ) : query.isError ? (
          <div className="px-5 py-8">
            <div role="alert" className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm dark:border-rose-500/25 dark:bg-rose-500/10">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" aria-hidden />
              <div className="flex-1">
                <p className="text-rose-700 dark:text-rose-300">
                  {isApiError(query.error)
                    ? query.error.firstErrorMessage
                    : 'Could not load verifications. Please try again.'}
                </p>
                <button
                  type="button"
                  onClick={() => query.refetch()}
                  className="mt-1.5 text-xs font-medium text-rose-600 underline-offset-2 hover:underline dark:text-rose-400"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState message="No verifications match the current filter." />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Transaction ID</TH>
                  {!isClient && <TH>Client</TH>}
                  <TH>Mobile</TH>
                  <TH>Operator</TH>
                  {!isClient && <TH>Country</TH>}
                  <TH>Service</TH>
                  <TH>Result</TH>
                  <TH className="text-right">Latency</TH>
                  <TH>Timestamp</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => (
                  <TR key={row.id}>
                    <TD><TxnId value={row.id} /></TD>
                    {!isClient && (
                      <TD className="text-xs text-fg-muted">
                        {row.clientName ?? (
                          <span className="font-mono text-[10px]">{row.clientId}</span>
                        )}
                      </TD>
                    )}
                    <TD className="font-mono text-xs">{row.mobileNumber ?? '—'}</TD>
                    <TD><OperatorBadge operator={row.operatorId} /></TD>
                    {!isClient && <TD><CountryBadge countryCode={row.countryCode} /></TD>}
                    <TD>
                      <Badge tone={row.id.startsWith('VNM-') ? 'warning' : 'primary'}>
                        {row.id.startsWith('VNM-') ? 'Verify Number Plus' : 'Verify Number'}
                      </Badge>
                    </TD>
                    <TD>
                      <Badge tone={row.isNumberValid ? 'success' : 'danger'}>
                        {row.isNumberValid ? 'MATCH' : 'NO MATCH'}
                      </Badge>
                    </TD>
                    <TD className="text-right text-xs text-fg-muted">
                      {row.latencyMs != null ? `${row.latencyMs}ms` : '—'}
                    </TD>
                    <TD className="text-[11px] text-fg-muted">
                      {new Date(row.createdAt).toLocaleString()}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-border px-4 py-3 sm:px-5">
                <span className="text-xs text-fg-muted">
                  Page {page + 1} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

